import React, { useEffect, useState, useCallback, useMemo, useRef } from "react";
import DatePicker from "react-datepicker";
import { format } from "date-fns";
import ar from "date-fns/locale/ar";
import enGB from "date-fns/locale/en-GB";
import "react-datepicker/dist/react-datepicker.css";
import TextInput from "../common/TextInput";
import {
  lockSlot,
  createAppointment,
  buildGoodConductAppointmentPayload,
} from "../../api/embassyGoodConductAppointment";

const MONTH_MAP = {
  jan: 0,
  feb: 1,
  mar: 2,
  apr: 3,
  may: 4,
  jun: 5,
  jul: 6,
  aug: 7,
  sep: 8,
  oct: 9,
  nov: 10,
  dec: 11,
};

/** Parses API date slot codes such as "01-May-2026" */
function parseDateSlotCode(code) {
  if (!code || typeof code !== "string") return null;
  const parts = code.split("-");
  if (parts.length !== 3) return null;
  const day = parseInt(parts[0], 10);
  const monKey = parts[1].toLowerCase().slice(0, 3);
  const month = MONTH_MAP[monKey];
  const year = parseInt(parts[2], 10);
  if (Number.isNaN(day) || month === undefined || Number.isNaN(year)) return null;
  return new Date(year, month, day);
}

function dayClassNameFactory(availableSet) {
  return (d) => {
    const time = d.getTime();
    if (!availableSet.has(time)) return null;
    if (d.getDay() === 5) return "gc-cal-day--friday";
    return null;
  };
}

function formatSlotRange(startTime, endTime) {
  const start = startTime || "";
  const end = endTime || "";
  if (!start && !end) return "";
  if (start && end) return `${start} - ${end}`;
  return start || end;
}

function extractSlotsPayload(payload) {
  if (Array.isArray(payload)) return payload;
  if (!payload || typeof payload !== "object") return null;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.slots)) return payload.slots;
  if (Array.isArray(payload.list)) return payload.list;
  if (payload.data && typeof payload.data === "object" && Array.isArray(payload.data.list)) {
    return payload.data.list;
  }
  return null;
}

const SLOTS_API_ERROR_KEYS = {
  NO_WEEKEND_BOOKING: "NO_WEEKEND_BOOKING",
};

function resolveSlotsApiError(parsedBody, fallbackMessage) {
  const code = parsedBody?.code;
  if (code && SLOTS_API_ERROR_KEYS[code]) {
    return SLOTS_API_ERROR_KEYS[code];
  }
  if (parsedBody && typeof parsedBody.message === "string" && parsedBody.message) {
    return parsedBody.message;
  }
  return fallbackMessage;
}

function GoodConductEnterDetails(props) {
  const {
    values,
    errors,
    dirty,
    handleOnChange,
    setData,
    navigation,
    t,
  } = props;

  const {
    firstName,
    lastName,
    passportNumber,
    emailAddress,
    mobileDialCode,
    mobileNumber,
    dateSlot,
    timeSlot,
    locale,
    goodConductShippingSelected,
  } = values;

  const [slotsForSelectedDate, setSlotsForSelectedDate] = useState([]);
  const [isSlotsLoading, setIsSlotsLoading] = useState(false);
  const [slotsError, setSlotsError] = useState("");
  const [appointmentBookingError, setAppointmentBookingError] = useState("");

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const mobileDisplay = `+${mobileDialCode || ""} ${mobileNumber || ""}`.trim();

  const dateFnsLocale = locale === "ar" ? ar : enGB;

  const selectedCalendarDate = useMemo(() => {
    if (!dateSlot) return null;
    return parseDateSlotCode(dateSlot);
  }, [dateSlot]);

  const availableDateKeySet = useMemo(() => {
    return new Set();
  }, []);


  const slotPanelDateLine = useMemo(() => {
    if (!selectedCalendarDate) return "";
    const pat = locale === "ar" ? "EEEE، d MMMM" : "EEEE, d MMMM";
    return format(selectedCalendarDate, pat, { locale: dateFnsLocale });
  }, [selectedCalendarDate, locale, dateFnsLocale]);

  const handleTimeSlotCard = useCallback(
    (slotId, label) => {
      setData({ name: "timeSlot", value: slotId });
      setData({ name: "timeSlotText", value: label });
    },
    [setData]
  );

  /** Avoid running reset on mount — setData would mark timeSlot dirty and show empty error immediately. */
  const skipDateSlotResetOnce = useRef(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (skipDateSlotResetOnce.current) {
      skipDateSlotResetOnce.current = false;
      return;
    }
    setData({ name: "timeSlot", value: "" });
    setData({ name: "timeSlotText", value: "" });
    setSlotsForSelectedDate([]);
    setSlotsError("");
  }, [dateSlot]);

  const selectedDateQuery = useMemo(() => {
    if (!selectedCalendarDate) return "";
    return format(selectedCalendarDate, "yyyy-MM-dd");
  }, [selectedCalendarDate]);

  useEffect(() => {
    if (!selectedDateQuery) return;
    const apiBaseUrl = process.env.REACT_APP_PRODUCTS_API_BASE_URL;
    const embassyCode = values.embassyCode;

    console.log("[Slots] effect triggered", {
      selectedDateQuery,
      apiBaseUrl,
      embassyCode,
      hasAccessToken: Boolean(values.accessToken),
    });

    if (!values.accessToken) {
      console.warn("[Slots] BLOCKED — no accessToken");
      setSlotsError("Unable to load slots.");
      return;
    }
    if (!apiBaseUrl || !embassyCode) {
      console.warn("[Slots] BLOCKED — missing apiBaseUrl or embassyCode", { apiBaseUrl, embassyCode });
      setSlotsError("Unable to load slots.");
      return;
    }

    const query = new URLSearchParams({
      Date: selectedDateQuery,
      EmbassyCode: embassyCode,
      ServiceTypeCode: "GOOD",
    }).toString();

    const fullUrl = `${apiBaseUrl}/services/embassy/api/slots?${query}`;
    console.log("[Slots] fetching →", fullUrl);

    setIsSlotsLoading(true);
    setSlotsError("");
    fetch(fullUrl, {
      method: "GET",
      headers: {
        accept: "text/plain",
        Authorization: `Bearer ${values.accessToken}`,
      },
    })
      .then(async (response) => {
        const rawBody = await response.text();
        console.log("[Slots] response status:", response.status, "| raw body:", rawBody);

        let parsedBody = null;
        try {
          parsedBody = rawBody ? JSON.parse(rawBody) : null;
        } catch (_) {
          // Keep parsedBody null if backend returns non-JSON text.
        }
        console.log("[Slots] parsedBody:", parsedBody);

        if (response.ok) {
          const slotsPayload = extractSlotsPayload(parsedBody);
          console.log("[Slots] extractSlotsPayload result:", slotsPayload);
          if (slotsPayload) {
            return slotsPayload;
          }
          if (parsedBody && (parsedBody.message || parsedBody.code)) {
            const errorMessage = resolveSlotsApiError(parsedBody, "Unable to load slots.");
            throw new Error(errorMessage);
          }
          return [];
        }

        let errorMessage = resolveSlotsApiError(parsedBody, "Unable to load slots.");
        if (errorMessage === "Unable to load slots." && rawBody && typeof rawBody === "string") {
          errorMessage = rawBody;
        }
        console.error("[Slots] API error →", errorMessage);
        throw new Error(errorMessage);
      })
      .then((data) => {
        const normalizedSlots = (Array.isArray(data) ? data : []).map((slot) => ({
          slotId: slot.slotId,
          date: slot.date,
          startTime: slot.startTime,
          endTime: slot.endTime,
          totalCapacity: Number(slot.totalCapacity) || 0,
          totalBooked: Number(slot.totalBooked) || 0,
          remaining: Number(slot.remaining) || 0,
          isFull: Boolean(slot.isFull),
        }));
        console.log("[Slots] normalizedSlots to render:", normalizedSlots);
        setSlotsForSelectedDate(normalizedSlots);
      })
      .catch((error) => {
        console.error("[Slots] caught error:", error);
        setSlotsError(error?.message || "Unable to load slots.");
      })
      .finally(() => {
        setIsSlotsLoading(false);
      });
  }, [
    selectedDateQuery,
    values.accessToken,
    values.embassyCode,
  ]);

  const handleDateSlotCalendar = useCallback(
    (date) => {
      if (!date) {
        setData({ name: "dateSlot", value: "" });
        return;
      }
      const formattedCode = format(date, "dd-MMM-yyyy", { locale: enGB });
      setData({ name: "dateSlot", value: formattedCode });
    },
    [setData]
  );

  const dateSlotFormClass =
    errors.dateSlot && dirty.dateSlot ? "form-group invalid" : "form-group";

  /** API already surfaced a slots error — hide generic required-field messaging underneath. */
  const showTimeSlotValidationMessage =
    Boolean(errors.timeSlot && dirty.timeSlot) && !slotsError;

  const timeSlotFormClass = showTimeSlotValidationMessage
    ? "form-group invalid"
    : "form-group";

  const dayClassNameCb = useMemo(
    () => dayClassNameFactory(availableDateKeySet),
    [availableDateKeySet]
  );

  const slotHeaderDisplay = useMemo(() => {
    if (!slotPanelDateLine) return "";
    return locale === "en" ? slotPanelDateLine.toUpperCase() : slotPanelDateLine;
  }, [locale, slotPanelDateLine]);

  const handleShippingChange = useCallback(
    (event) => {
      setData({
        name: "goodConductShippingSelected",
        value: event.target.checked,
      });
    },
    [setData]
  );

  const stepInputs = [
    "firstName",
    "lastName",
    "passportNumber",
    "emailAddress",
    "dateSlot",
    "timeSlot",
  ];

  const isStepFieldEmpty = (name, val) => {
    if (val === null || val === undefined) return true;
    if (name === "timeSlot") {
      return val === "";
    }
    return val === "";
  };

  const isStepInvalid = () => {
    const nullValues = stepInputs.filter((input) =>
      isStepFieldEmpty(input, values[input])
    );
    const errorValues = stepInputs.filter((input) => errors[input]);
    return Boolean(nullValues.length || errorValues.length);
  };

  const setOverlayLoader = useCallback(
    (status, loaderMessage) => {
      setData({ name: "isLoading", value: status });
      setData({
        name: "loaderText",
        value: loaderMessage || t("LOADING_PLEASE_WAIT"),
      });
      if (status === true) {
        document.body.classList.add("no-scroll");
      } else {
        document.body.classList.remove("no-scroll");
      }
    },
    [setData, t]
  );

  const handleProceed = async () => {
    setAppointmentBookingError("");
    if (isStepInvalid()) {
      stepInputs.forEach((name) => {
        setData({ name, value: values[name] });
      });
      window.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    setOverlayLoader(true, t("GC_APPOINTMENT_SUBMITTING"));
    try {
      const slotId = Number(values.timeSlot);
      const { lockId } = await lockSlot({
        accessToken: values.accessToken,
        slotId,
        passportNumber: values.passportNumber,
      });
      const payload = buildGoodConductAppointmentPayload(values, lockId);
      const { appointmentReference } = await createAppointment({
        accessToken: values.accessToken,
        body: payload,
      });
      setData({
        name: "goodConductAppointmentReference",
        value: appointmentReference,
      });
      if (values.goodConductShippingSelected) {
        navigation.go("shipping-sender");
      } else {
        navigation.go("good-booking-success");
      }
    } catch (err) {
      const msg =
        err && typeof err.message === "string" && err.message.trim()
          ? err.message.trim()
          : t("GC_APPOINTMENT_ERROR");
      setAppointmentBookingError(msg);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } finally {
      setOverlayLoader(false);
    }
  };

  const proceedDisabled = values.isLoading || isStepInvalid();

  return (
    <div className="good-conduct-enter mt-4 mb-5">
      <div className="row align-items-center mb-4 gc-page-header">
        <div className="col-auto">
          <img
            src="./images/user-icon.svg"
            alt=""
            className="gc-section-icon"
          />
        </div>
        <div className="col">
          <h3 className="mb-0 gc-page-title">{t("GC_ENTER_YOUR_DETAILS_TITLE")}</h3>
        </div>
      </div>

      <div className="gc-section-card mb-4">
        <div className="gc-section-heading">
          <img src="./images/user-icon.svg" alt="" className="gc-inline-icon" />
          <span>{t("GC_PERSONAL_DETAILS")}</span>
        </div>
        <div className="row">
          <div className="col-md-6 mb-3">
            <TextInput
              id="gc-firstName"
              name="firstName"
              label={t("FIRST_NAME")}
              placeholder=""
              onChange={handleOnChange}
              value={firstName}
              validation={{ errors, dirty }}
              required
              t={t}
            />
          </div>
          <div className="col-md-6 mb-3">
            <TextInput
              id="gc-lastName"
              name="lastName"
              label={t("LAST_NAME")}
              placeholder=""
              onChange={handleOnChange}
              value={lastName}
              validation={{ errors, dirty }}
              required
              t={t}
            />
          </div>
          <div className="col-md-6 mb-3">
            <TextInput
              id="gc-passportNumber"
              name="passportNumber"
              label={t("PASSPORT_NUMBER")}
              placeholder=""
              onChange={handleOnChange}
              value={passportNumber}
              validation={{ errors, dirty }}
              required
              t={t}
            />
          </div>
          <div className="col-md-6 mb-3">
            <div className="form-group">
              <label htmlFor="gc-mobile-readonly">
                {t("MOBILE_NUMBER")} <span>*</span>
              </label>
              <input
                id="gc-mobile-readonly"
                type="text"
                className="form-control gc-disabled-field always-ltr"
                value={mobileDisplay}
                disabled
                readOnly
                aria-readonly="true"
              />
            </div>
          </div>
          <div className="col-12 mb-3">
            <TextInput
              id="gc-emailAddress"
              name="emailAddress"
              label={t("EMAIL_ADDRESS")}
              placeholder=""
              onChange={handleOnChange}
              value={emailAddress}
              validation={{ errors, dirty }}
              required
              maxLength={100}
              t={t}
            />
          </div>
        </div>
      </div>

      <div className="gc-section-card mb-4">
        <div className="gc-section-heading gc-section-heading--navy">
          <img src="./images/calendar.svg" alt="" className="gc-inline-icon" />
          <span>{t("GC_APPOINTMENT_SLOT")}</span>
        </div>
        {/* <div
          className="gc-appointment-banner-ref mb-4"
          role="region"
          aria-label={t("GC_SLOT_RULES_TITLE")}
        >
          <p className="mb-0">{t("GC_SLOT_RULES_COMPACT")}</p>
        </div> */}
        <div className="row g-4 gc-appointment-row">
          <div className="col-lg-5 mb-3 mb-lg-0">
            <div className={dateSlotFormClass}>
              <span className="visually-hidden" id="gc-dateSlot-label">
                {t("PREFERRED_DATE")} *
              </span>
              <>
                <div
                  id="gc-dateSlot-calendar"
                  className="gc-date-calendar-surface gc-date-calendar-surface--ref"
                  role="group"
                  aria-labelledby="gc-dateSlot-label"
                  tabIndex={-1}
                  aria-invalid={errors.dateSlot && dirty.dateSlot ? true : undefined}
                >
                  <DatePicker
                    inline
                    selected={selectedCalendarDate}
                    onChange={handleDateSlotCalendar}
                    minDate={new Date()}
                    locale={locale === "ar" ? ar : enGB}
                    calendarClassName="gc-datepicker-calendar"
                    showPopperArrow={false}
                    fixedHeight
                    dayClassName={dayClassNameCb}
                  />
                </div>
                {errors.dateSlot && dirty.dateSlot ? (
                  <div className="error-message mt-2">{t(errors.dateSlot)}</div>
                ) : null}
                {/* <p className="small text-muted mb-0 mt-2">{t("GC_TAP_DATE_HINT")}</p> */}
              </>
            </div>
          </div>
          <div className="col-lg-7 mb-3 mb-lg-0">
            <div className={timeSlotFormClass}>
              <div className="gc-slot-panel">
                {selectedCalendarDate && slotHeaderDisplay ? (
                  <p className="gc-slot-panel__title">
                    <span className="gc-slot-panel__date">{slotHeaderDisplay}</span>
                    <span className="gc-slot-panel__sep"> — </span>
                    <span className="gc-slot-panel__action">{t("GC_SELECT_TIME_SLOT")}</span>
                  </p>
                ) : (
                  <p className="gc-slot-panel__title gc-slot-panel__title--muted">
                    {t("GC_SELECT_DATE_FIRST")}
                  </p>
                )}

                <div className="gc-slot-cards" role="list">
                  {selectedCalendarDate && isSlotsLoading && (
                    <div className="gc-slot-loader" role="status" aria-live="polite">
                      <span className="gc-slot-loader__spinner" aria-hidden="true" />
                      <span className="gc-slot-loader__text">Loading slots...</span>
                    </div>
                  )}
                  {selectedCalendarDate && !isSlotsLoading && slotsError && (
                    <div className="gc-slot-error-alert" role="alert">
                      <img src="./images/warning-circle.svg" alt="" className="gc-slot-error-alert__icon" aria-hidden="true" />
                      <span>{t(slotsError, { defaultValue: slotsError })}</span>
                    </div>
                  )}
                  {selectedCalendarDate &&
                    !isSlotsLoading &&
                    !slotsError &&
                    slotsForSelectedDate.length === 0 && (
                      <p className="small text-muted mb-0">{t("GC_NO_DATES_AVAILABLE")}</p>
                    )}
                  {selectedCalendarDate &&
                    !isSlotsLoading &&
                    !slotsError &&
                    slotsForSelectedDate.map((item) => {
                      const slotRange = formatSlotRange(item.startTime, item.endTime);
                      const isActive = String(timeSlot) === String(item.slotId);
                      const isDisabled = item.isFull || item.remaining <= 0;
                      return (
                        <button
                          key={item.slotId}
                          type="button"
                          role="listitem"
                          className={`gc-slot-card${isActive ? " gc-slot-card--selected" : ""}`}
                          onClick={() => handleTimeSlotCard(item.slotId, slotRange)}
                          disabled={isDisabled}
                        >
                          <div className="gc-slot-card__top">
                            <div className="gc-slot-card__left">
                              <div className="gc-slot-card__time">{slotRange}</div>
                              <div className="gc-slot-card__sub">
                                {isDisabled ? t("GC_LEGEND_BOOKED") : t("GC_SELECT_TIME_SLOT")}
                              </div>
                            </div>
                          </div>
                        </button>
                      );
                    })}
                </div>
                {showTimeSlotValidationMessage ? (
                  <div className="error-message mt-2">{t(errors.timeSlot)}</div>
                ) : null}

              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="gc-section-card gc-shipment-card mb-4">
        <div className="gc-section-heading">
          <img src="./images/billing-icon.svg" alt="" className="gc-inline-icon" />
          <span>{t("GC_SHIPMENT_OPTIONS")}</span>
        </div>
        <div className="gc-shipment-option">
          <label className="gc-shipment-option__inner" htmlFor="gc-shipping">
            <input
              id="gc-shipping"
              type="checkbox"
              className="gc-shipment-switch-input visually-hidden"
              checked={!!goodConductShippingSelected}
              onChange={handleShippingChange}
              aria-labelledby="gc-shipping-title"
            />
            <span className="gc-shipment-switch" aria-hidden="true" />
            <div className="gc-shipment-option__body">
              <div className="gc-shipment-option__row">
                <span id="gc-shipping-title" className="gc-shipment-option__title">
                  {t("GC_DHL_PREMIUM_LABEL")}
                </span>
                <span className="gc-price-pill">{t("GC_DHL_PRICE")}</span>
              </div>
              <p className="gc-shipment-option__desc">{t("GC_DHL_DESCRIPTION")}</p>
              <div className="gc-shipment-meta">{t("GC_DHL_INCLUDES")}</div>
            </div>
          </label>
        </div>
      </div>

      {appointmentBookingError ? (
        <div className="alert alert-danger mb-4" role="alert">
          {appointmentBookingError}
        </div>
      ) : null}

      <div className="row mt-5">
        <div className="col-6 dir-left">
          <button
            type="button"
            className="btn btn-lg btn-default btn-back"
            onClick={() => navigation.go("contact-verification")}
          >
            <img src="./images/arrow-left.svg" alt="" />
            {t("BACK")}
          </button>
        </div>
        <div className="col-6 dir-right">
          <button
            type="button"
            className="btn btn-primary btn-lg emx-button"
            onClick={handleProceed}
            disabled={proceedDisabled}
          >
            {goodConductShippingSelected
              ? t("GC_PROCEED_APPOINTMENT_AND_SHIPMENT")
              : t("GC_CREATE_APPOINTMENT")}
          </button>
        </div>
      </div>
    </div>
  );
}

export default GoodConductEnterDetails;
